import { observable, action } from 'mobx';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { IOption } from 'cd-eft/components/common/ComboBox';
import * as PRJAPI from 'cd-eft/services/eftPrjAdminApi';

export interface IErrorMessage {
  editingStatus: string;
  errorCode: string;
  errorCategoryCode: string;
  errorShortDescription: string;
  errorDetails: string;
  loggingFlag: string;
  readonly lastChangedBy: string;
  readonly lastChangeDate: string;
  url: string;
  readonly errorCodeLUTId: number;
  originalErrorCode?: string;
  errorCategoryId: number;
  errorCodeStatus: string;
  errorSubCategoryCode: string;
  moduleCode: string;
  designArea: string;
}

const initialErrorMessage: IErrorMessage = {
  editingStatus: '',
  errorCode: '',
  errorCategoryCode: '',
  errorShortDescription: '',
  loggingFlag: '',
  errorDetails: '',
  lastChangedBy: '',
  lastChangeDate: '',
  url: '',
  errorCodeLUTId: 0,
  errorSubCategoryCode: '',
  moduleCode: '',
  originalErrorCode: '',
  errorCategoryId: 0,
  errorCodeStatus: '',
  designArea: '',
};

const errorCategoryId = {
  ERROR: 3,
  WARNING: 2,
  INFORMATION: 1,
};

export class AdminErrorMessageVM {
  history: any;

  constructor(history) {
    this.history = history;
  }

  @observable message: IErrorMessage = { ...initialErrorMessage };
  @observable isLoading: boolean = false;
  @observable lookingForCode: boolean = false;
  @observable loadingMessage: string = '';
  @observable errorCategoryCodes: IOption[] = [];
  @observable errorSubCategoryCodes: any[] = [];
  @observable moduleCodes: any[] = [];
  @observable designAreas: IOption[] = [];
  @observable newFullErrorCode: string = '';
  @observable deleteErrorMessageModalIsOpen: boolean = false;

  @action.bound toggleDeleteErrorMessageModalIsOpen = () =>
    (this.deleteErrorMessageModalIsOpen = !this.deleteErrorMessageModalIsOpen);

  @action.bound deleteErrorMessage = async () => {
    console.log('Deleting Error Message()');
    this.setIsLoading(true, 'Deleting Error Message...');
    try {
      const response: any = await SETAPI.ErrorHandlingController.deleteErrorHandlingByErrorCodeLUTIdUsingPOST(
        this.message.errorCodeLUTId
      );
      if (response) {
        this.toggleDeleteErrorMessageModalIsOpen();
        this.history.push(`/admin/error-messages`);
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      this.setIsLoading(false);
    }
  };

  @action.bound setIsLoading = (value: boolean, message?: string) => {
    this.isLoading = value;
    this.loadingMessage = message ? message : '';
  };

  @action.bound init = async (messageId: string) => {
    this.setIsLoading(true, 'Loading Error Message...');
    try {
      const response: any = await SETAPI.ErrorHandlingController.getErrorHandlingByOriginalErrorCodesUsingGET(
        messageId
      );
      if (response.data) this.message = response.data[0];
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      this.setIsLoading(false);
    }
  };

  @action.bound initSelection = async () => {
    this.setIsLoading(true, 'Loading...');
    try {
      const response: any = await PRJAPI.ParameterController.getDefaultParameterByNameUsingGET('errorDropdownParams');
      if (response && response.data && response.data.json) {
        const json = JSON.parse(response.data.json);
        this.errorCategoryCodes.length = 0;
        this.errorSubCategoryCodes.length = 0;
        json.errorCategory.forEach((el) => this.errorCategoryCodes.push({ name: el, value: el }));
        json.categoryCode.forEach((el) =>
          this.errorSubCategoryCodes.push({
            name: el.errorSubCategoryCode,
            value: el.errorSubCategoryCode,
            childModules: el.moduleCode,
          })
        );
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      this.setIsLoading(false);
    }
  };

  @observable formValid = {
    errorShortDescription: true,
    errorDetails: true,
  };

  @action.bound setValue = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    this.lookingForCode = true;
    this.message[name] = value;

    if (name === 'errorCategoryCode') this.message.errorCategoryId = errorCategoryId[value];

    if (name === 'errorSubCategoryCode') {
      this.message.originalErrorCode = '';
      this.message.errorCode = '';
      this.message.moduleCode = '';
      this.message.designArea = '';
      this.moduleCodes.length = 0;
      this.designAreas.length = 0;
      if (value === '') {
        return;
      } else {
        this.errorSubCategoryCodes
          .find((el) => el.name === value)
          .childModules.forEach((el) =>
            this.moduleCodes.push({ name: el.moduleCodeName, value: el.moduleCodeName, childArias: el.designArea })
          );
      }
    }

    if (name === 'moduleCode') {
      this.message.originalErrorCode = '';
      this.message.errorCode = '';
      this.message.designArea = '';
      this.designAreas.length = 0;

      if (value === '') {
        return;
      } else if (value === 'HTTP') {
        this.designAreas = [{ name: 'no selection', value: 'no selection' }];
      } else {
        this.moduleCodes
          .find((el) => el.name === value)
          .childArias.forEach((el) => this.designAreas.push({ name: el, value: el }));
      }
    }

    if (name === 'designArea') {
      this.message.originalErrorCode = '';
      this.message.errorCode = '';
    }

    if (
      this.message.errorCategoryCode &&
      this.message.errorSubCategoryCode &&
      this.message.moduleCode &&
      this.message.designArea
    ) {
      const payload: any = {
        errorCategory: this.message.errorCategoryCode,
        categoryCode: this.message.errorSubCategoryCode,
        moduleCode: this.message.moduleCode,
        designArea: this.message.designArea === 'no selection' ? '' : this.message.designArea,
      };

      try {
        const response: any = await SETAPI.ErrorHandlingController.getNextErrorCodeNumberUsingPOST(payload);
        if (response && response.data) {
          const area = this.message.designArea === 'no selection' ? '' : `${this.message.designArea}_`;
          this.message.originalErrorCode = `${area}${response.data.nextErrorCodeNum}`;
          this.message.errorCode = `${this.message.errorCategoryId}_${this.message.errorSubCategoryCode}_${this.message.moduleCode}_${this.message.originalErrorCode}`;
        }
      } catch (error) {
        MessageCenter.addError((error as Error).message);
      }
      this.lookingForCode = false;
    } else {
      this.message.originalErrorCode = '';
      this.message.errorCode = '';
    }
  };

  @action.bound setCheckboxValue(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, checked } = event.target;
    this.message[name] = checked ? 'Y' : 'N';
  }

  @action.bound setTextValue(event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) {
    const { name, value } = event.target;
    this.message[name] = value;
    this.formValid[name] = value.length > 0 && this.message[name].trim() !== '';
  }

  @action.bound onSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    this.setIsLoading(true, 'Updating Error Message...');
    try {
      let response: any;
      if (this.message.errorCodeLUTId) {
        response = await SETAPI.ErrorHandlingController.updateErrorHandlingUsingPOST(this.message);
      } else {
        const payload = { ...this.message };
        response = await SETAPI.ErrorHandlingController.createErrorHandlingLUTUsingPOST(payload);
      }
      if (response) {
        this.history.push(`/admin/error-messages`);
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      this.setIsLoading(false);
    }
  };
}
